// src/features/engine/context/ProgramEngineContext.jsx

import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useProgramScopedLocale } from "../../program-localization/ProgramScopedLocaleContext";
import { resolveBrowserCompletionTimeZone } from "../utils/completionTimeZone";

const ProgramEngineContext = createContext();

export function ProgramEngineProvider({
  projectSlug: projectSlugProp,
  program,
  attempt,
  programState: initialProgramState,
  apiUrl,
  token,
  children,
  initialUIState,
  projectMode,
  projectUIFlow
}) {

  const navigate = useNavigate();
  const { slug: slugFromParams } = useParams();
  const programScopedLocale = useProgramScopedLocale();

  const projectSlug = projectSlugProp || slugFromParams;

  const [canonicalProgram, setCanonicalProgram] = useState(program);
  const [canonicalAttempt, setCanonicalAttempt] = useState(attempt);

  useEffect(() => setCanonicalProgram(program), [program]);
  useEffect(() => setCanonicalAttempt(attempt), [attempt]);

  const [state, setState] = useState(initialUIState || "intro");

  // 🔥 BACKEND STATE
  const [programState, setProgramState] = useState(initialProgramState);

  // =====================================================
  // 🔥 SYNC UI STATE WITH BACKEND (SOURCE OF TRUTH)
  // =====================================================

  useEffect(() => {

    if (!programState) return;

    if (programState.isProgramCompleted) {
      setState("completion");
      return;
    }

    if (programState.currentStep) {
      setState("running");
      return;
    }

    setState("intro");

  }, [programState]);

  // =====================================================
  // CURRENT STEP
  // =====================================================

  const steps = canonicalProgram?.steps || [];

  const currentStep = steps.find(
    s => s.key === programState?.currentStep
  ) || null;

  // =====================================================
  // UI NAVIGATION (EXPLICIT TRANSITIONS)
  // =====================================================

  const startProgram = async () => {

    // 🔥 important: força backend sync abans d’entrar a running
    const updatedState = await refetchProgramState();

    if (updatedState?.currentStep) {
      setState("running");
    } else {
      console.warn("⚠️ startProgram: no currentStep from backend");
    }

  };

  const goToResults = () => {
    setState("results");
  };

  const completeFirstOpenPresentation = async () => {
    const presentation = programState?.firstOpenPresentation;
    if (presentation?.status !== "presentation_required") return presentation;
    const res = await fetch(`${apiUrl}/programs/${program.programSlug}/presentation/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        projectId: attempt.projectId,
        programAttemptId: attempt._id,
        presentationId: presentation.presentationId,
      }),
    });
    if (!res.ok) throw new Error("Failed to complete Program presentation");
    const data = await res.json();
    setProgramState(current => ({ ...current, firstOpenPresentation: data.firstOpenPresentation }));
    return data.firstOpenPresentation;
  };

  // =====================================================
  // REFETCH PROGRAM STATE
  // =====================================================

  const refetchProgramState = async () => {

    try {

    if (!canonicalProgram || !canonicalAttempt) return;

      const res = await fetch(
        `${apiUrl}/programs/${canonicalProgram.programSlug}/state?projectId=${canonicalAttempt.projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to refetch program state");
      }

      const data = await res.json();

      console.log("🔄 Program state refreshed", data);

      const resolvedState = data.state || data;
      if (data.program) setCanonicalProgram(data.program);
      if (data.attempt) setCanonicalAttempt(data.attempt);
      setProgramState(resolvedState);

      return {
        ...resolvedState,
        program: data.program || canonicalProgram,
        attempt: data.attempt || canonicalAttempt,
      };

    } catch (err) {
      console.error("❌ refetchProgramState error:", err);
    }

  };

  // =====================================================
  // COMPLETE STEP
  // =====================================================

  const navigateToProgramDashboard = () => {
    if (!projectSlug) {
      console.error("❌ projectSlug undefined (CRITICAL)");
      return;
    }
    const search = programScopedLocale?.isEnabled
      ? programScopedLocale.buildSearchWithLocale({ step: null, review: null })
      : "";
    navigate(`/app/project/${projectSlug}${search}`);
  };

  const programStepPath = (stepKey, review = null) => {
    if (!projectSlug || !stepKey) return;
    const search = programScopedLocale?.isEnabled
      ? programScopedLocale.buildSearchWithLocale({ step: stepKey, review })
      : `?step=${encodeURIComponent(stepKey)}${review ? `&review=${encodeURIComponent(review)}` : ""}`;
    return `/app/project/${projectSlug}/run/${canonicalProgram.programSlug}${search}`;
  };

  const navigateToProgramStep = (stepKey, { compositeBackStepKey = null } = {}) => {
    const destination = programStepPath(stepKey);
    if (!destination) return;
    if (compositeBackStepKey) {
      const compositeBackPath = programStepPath(compositeBackStepKey, "completed");
      navigate(compositeBackPath, { replace: true });
    }
    navigate(destination);
  };

  const completeCurrentStep = async (sessionId, { deferNavigation = false } = {}) => {

    try {

      const programAttemptId = canonicalAttempt?._id;

      const completionTimeZone = resolveBrowserCompletionTimeZone();

      const res = await fetch(`${apiUrl}/programs/submit-step`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          programSlug: canonicalProgram.programSlug,
          programAttemptId,
          completionTimeZone
        })
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("❌ completeStep backend error:", text);
        throw new Error(`Failed (${res.status})`);
      }

      const data = await res.json();

      console.log("✅ Step completed:", data);

      if (deferNavigation) return data;

      // =====================================================
      // 🔥 TRANSICIÓ CONTROLADA
      // =====================================================

      if (projectUIFlow?.autoAdvance) {

        const updatedState = await refetchProgramState();

        if (updatedState?.isProgramCompleted) {
          setState("completion");
        } else {
          setState("running");
        }

      } else {

        navigateToProgramDashboard();

      }

    } catch (err) {
      console.error("❌ completeStep error:", err);
      if (deferNavigation) throw err;
    }

  };

  return (
    <ProgramEngineContext.Provider
      value={{
        projectSlug,
        program: canonicalProgram,
        attempt: canonicalAttempt,
        programState,
        apiUrl,
        token,
        state,
        currentStep,
        projectMode,
        projectUIFlow,
        programScopedLocale,
        startProgram,
        goToResults,
        completeFirstOpenPresentation,
        completeCurrentStep,
        navigateToProgramDashboard,
        navigateToProgramStep,
        refetchProgramState
      }}
    >
      {children}
    </ProgramEngineContext.Provider>
  );
}

export function useProgramEngine() {
  return useContext(ProgramEngineContext);
}
